/**
 * BlogStudioView — top-level Blog tab content.
 *
 * Renders the actual exported HTML inside a sandboxed iframe — what
 * you see here is byte-identical to what gets pasted into Ghost /
 * WordPress when the user uses the unified Share button at the top.
 *
 * No floating toolbar — all actions (publish, copy-for-platform,
 * export) live in the unified Share dropdown in the workspace top
 * toolbar. Edits route through chat (the embedded ivy-chat store).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { Loader2, Sparkles, Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { blogAPI } from '@/services/blogAPI';
import { useBlogPost } from '@/hooks/useBlogPost';
import { cn } from '@/lib/utils';

// localStorage flag for refresh-survival of generation state
const GEN_KEY = (id: string) => `ivy:blog:generating:${id}`;
const GEN_TIMEOUT_MS = 5 * 60 * 1000;

function readLocalGenerating(id: string): boolean {
  try {
    const raw = localStorage.getItem(GEN_KEY(id));
    if (!raw) return false;
    const t = Number(raw);
    return Number.isFinite(t) && Date.now() - t < GEN_TIMEOUT_MS;
  } catch {
    return false;
  }
}
function writeLocalGenerating(id: string, on: boolean) {
  try {
    if (on) localStorage.setItem(GEN_KEY(id), String(Date.now()));
    else localStorage.removeItem(GEN_KEY(id));
  } catch {
    /* ignore */
  }
}

interface BlogStudioViewProps {
  conversationId: string;
}

export const BlogStudioView: React.FC<BlogStudioViewProps> = ({ conversationId }) => {
  const [polling] = useState(true);
  const [localGenerating, setLocalGenerating] = useState(() =>
    readLocalGenerating(conversationId),
  );
  const { data, loading, error } = useBlogPost(conversationId, { poll: polling });

  // Iframe HTML state
  const [iframeHtml, setIframeHtml] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const lastVersionRef = useRef<number | null>(null);

  // Effective generating: server flag OR local optimistic
  const generating = (data?.generating ?? false) || localGenerating;

  // sendMessage helper for the Generate CTA — same path as before.
  const sendToChat = useCallback((message: string) => {
    const send = () => {
      const store = useEmbeddedIvyChatStore.getState();
      if (!store.chatId) {
        setTimeout(send, 250);
        return;
      }
      void store.sendMessage(message);
    };
    send();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    setLocalGenerating(true);
    writeLocalGenerating(conversationId, true);
    try {
      await blogAPI.markGenerating(conversationId);
    } catch (err) {
      console.warn('[BlogStudioView] markGenerating failed', err);
    }
    sendToChat('Write a blog companion for this video.');
    toast.success('Drafting your blog companion…');
  }, [conversationId, generating, sendToChat]);

  // Refetch the iframe HTML whenever the doc version changes.
  useEffect(() => {
    if (!data?.exists) {
      setIframeHtml(null);
      return;
    }
    const v = data.meta?.version ?? 0;
    if (v === lastVersionRef.current) return;
    lastVersionRef.current = v;
    setIframeLoading(true);
    blogAPI
      .export(conversationId, 'branded')
      .then((r) => setIframeHtml(r.content))
      .catch((err) => {
        console.warn('[BlogStudioView] preview export failed', err);
        setIframeHtml(null);
      })
      .finally(() => setIframeLoading(false));
  }, [conversationId, data?.exists, data?.meta?.version]);

  // Clear local generating flag once doc lands
  useEffect(() => {
    if (data?.exists) {
      writeLocalGenerating(conversationId, false);
      setLocalGenerating(false);
    }
  }, [conversationId, data?.exists]);

  // Auto-clear local flag after timeout (in case agent silently failed)
  useEffect(() => {
    if (!localGenerating) return;
    const startedRaw = localStorage.getItem(GEN_KEY(conversationId));
    const startedAt = startedRaw ? Number(startedRaw) : Date.now();
    const remaining = Math.max(0, GEN_TIMEOUT_MS - (Date.now() - startedAt));
    const t = setTimeout(() => {
      writeLocalGenerating(conversationId, false);
      setLocalGenerating(false);
    }, remaining);
    return () => clearTimeout(t);
  }, [conversationId, localGenerating]);

  // Iframe sandbox needs allow-scripts so the nested video player runs.
  const iframeSrcDoc = useMemo(() => {
    if (!iframeHtml) return null;
    return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;}body{background:#fff;}</style></head><body>${iframeHtml}</body></html>`;
  }, [iframeHtml]);

  // ─── Branches ─────────────────────────────────────────────────────────

  if (loading && !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  // Drafting state — agent is running, no doc yet
  if (generating && (!data || !data.exists)) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12 studio-canvas">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <h3 className="text-lg font-semibold mb-2">Drafting your blog companion…</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-2">
          Reading your video's outline and narration, then composing the piece. Watch the
          chat on the left for live progress.
        </p>
        <p className="text-xs text-muted-foreground/70 max-w-sm">
          Usually 30–90 seconds. The blog appears here when ready.
        </p>
      </div>
    );
  }

  // Empty state — no blog yet
  if (!data || !data.exists) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12 studio-canvas">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5 bg-primary/20">
          <Newspaper className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Write a blog companion</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-6">
          A written piece that mirrors your video's story — ready to share or paste into
          Substack, Ghost, WordPress, LinkedIn, Medium, or your own site.
        </p>
        <Button onClick={handleGenerate} size="default" className="gap-2">
          <Sparkles className="w-4 h-4" />
          Generate blog companion
        </Button>
        <p className="text-xs text-muted-foreground mt-5 max-w-md">
          Refine afterward by chatting — "shorter intro", "more casual", "swap the hero
          image". Use the Share button up top to publish or copy for any platform.
        </p>
      </div>
    );
  }

  // Render — full-bleed iframe preview, no chrome
  return (
    <div className="flex-1 min-h-0 flex flex-col relative studio-canvas">
      <div className={cn('flex-1 min-h-0 p-6 md:p-8 flex items-stretch justify-center')}>
        <div className="w-full max-w-[860px] flex-1 rounded-xl overflow-hidden shadow-xl ring-1 ring-black/5 dark:ring-white/10 bg-white relative">
          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/70 backdrop-blur-sm">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {iframeSrcDoc ? (
            <iframe
              key={data.meta?.version ?? 'preview'}
              title="Blog preview"
              srcDoc={iframeSrcDoc}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
              allow="autoplay; fullscreen; clipboard-write"
              className="w-full h-full border-0 bg-white"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
        </div>
      </div>
      {/* Subtle bottom hint pointing users to the unified Share button up top */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-background/95 backdrop-blur border border-border/40 rounded-full shadow-sm px-3 py-1 text-[11px] text-muted-foreground pointer-events-none">
        Edit by chatting · Use <span className="font-medium text-foreground/80">Share</span> up top to publish or copy for any platform
      </div>
    </div>
  );
};

export default BlogStudioView;
